import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../i18n/LanguageContext";

const AboutIntroSection: React.FC = () => {
  const { t } = useLanguage();
  const sectionRef = useRef<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const intro = (t as any)?.homeIntro || {};
  const titlePrefix =
    intro.titlePrefix || "We've been doing this for over";
  const titleHighlight = intro.titleHighlight || "10 years";
  const description =
    intro.description ||
    "EduVate makes studying in Egypt simple for families from Jordan, Sudan, Yemen, and across the Arab world. We understand the schools, systems, and paperwork and what matters most to parents. From your first call to your child's first day, we're with you every step of the way.";
  const cta = intro.cta || "Learn More About Us";

  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;

    if (!("IntersectionObserver" in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px -15% 0px", threshold: 0.25 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="site-container flex min-h-[80vh] items-center justify-center overflow-hidden bg-white py-24 sm:min-h-screen sm:py-28"
      aria-labelledby="home-intro-title"
    >
      <div
        className={`inline-flex w-full max-w-[1177px] flex-col items-center justify-start gap-6 text-center transition-all duration-700 ease-out ${
          isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
        }`}
      >
        <div className="relative flex w-full flex-col items-center justify-start gap-3">
          <h2
            id="home-intro-title"
            className="header-title relative max-w-5xl uppercase text-teal-900"
          >
            <span>{titlePrefix} </span>
            <span className="relative inline-block px-2 sm:px-3">
              <span
                className="absolute inset-x-0 top-1/2 h-[1.42em] -translate-y-1/2 bg-main-color"
                aria-hidden="true"
              />
              <span className="relative z-10">{titleHighlight}</span>
            </span>
          </h2>
          <p className="section-subtitle relative max-w-[842px] text-neutral-600">
            {description}
          </p>
        </div>

        <Link
          to="/about"
          data-property-1="prim"
          className="inline-flex min-h-10 w-full items-center justify-center gap-2.5 bg-second-color px-6 py-2 text-center font-sans text-base font-medium leading-6 text-main-color transition-colors hover:bg-second-color/90 focus:outline-none focus:ring-2 focus:ring-main-color focus:ring-offset-2 focus:ring-offset-white sm:w-fit"
        >
          {cta}
        </Link>
      </div>
    </section>
  );
};

export default AboutIntroSection;
